import React from 'react';
import { t } from '../../translation';

interface CameraSlidersProps {
    className?: string;
}

const MIN_ZOOM = 3;
const MAX_ZOOM = 15;
const MAX_LIFT = 1;
const MIN_LIFT = -1;

const CameraSliders: React.FC<CameraSlidersProps> = ({ className = '' }) => {
    const [zoomValue, setZoomValue] = React.useState(8);
    const [liftValue, setLiftValue] = React.useState(50);
    const [panHorizontal, setPanHorizontal] = React.useState(0);

    const baseOriginRef = React.useRef<any>(null);
    const baseLiftOriginYRef = React.useRef<number | null>(null);
    const currentLiftRef = React.useRef(0);

    const updateCamera = React.useCallback((zoom: number) => {
        if (window.viewer) {
            const viewer = window.viewer;
            const multiCamera = viewer.multiCamera;
            const newDistance = MAX_ZOOM - (zoom - MIN_ZOOM);
            multiCamera.sceneSize = newDistance * 0.5;
            multiCamera._zoomDist = newDistance;
            viewer.renderNextFrame();
        }
    }, []);

    const updatePan = React.useCallback((pan: number) => {
        if (window.viewer) {
            const viewer = window.viewer;
            const multiCamera = viewer.multiCamera;
            const camera = viewer.camera.camera;
            const right = camera.entity.right.clone();
            const panDistance = (pan / 100) * 5;
            const offset = right.mulScalar(panDistance);

            if (multiCamera._origin) {
                if (!baseOriginRef.current) {
                    baseOriginRef.current = multiCamera._origin.clone();
                }
                multiCamera._origin.copy(baseOriginRef.current);
                multiCamera._origin.add(offset);
            }
            viewer.renderNextFrame();
        }
    }, []);

    const updateModelLift = React.useCallback((lift: number) => {
        if (window.viewer) {
            const viewer = window.viewer;
            const multiCamera = viewer.multiCamera;

            if (multiCamera._origin) {
                if (baseLiftOriginYRef.current === null) {
                    baseLiftOriginYRef.current = multiCamera._origin.y;
                }
                multiCamera._origin.y = baseLiftOriginYRef.current - lift;
            }
            viewer.renderNextFrame();
        }
    }, []);

    const handleZoomChange = React.useCallback((value: number) => {
        setZoomValue(value);
        updateCamera(value);
    }, [updateCamera]);

    const handlePanHorizontal = React.useCallback((value: number) => {
        setPanHorizontal(value);
        updatePan(value);
    }, [updatePan]);

    const handleModelLift = React.useCallback((value: number) => {
        const range = MAX_LIFT - MIN_LIFT;
        const lift = MIN_LIFT + ((100 - value) / 100) * range;
        currentLiftRef.current = lift;
        setLiftValue(value);
        updateModelLift(lift);
    }, [updateModelLift]);

    return (
        <div className={`camera-sliders ${className}`}>
            {/* Zoom Slider */}
            <div className="camera-sliders__control">
                <label className="camera-sliders__label">{t('position.zoom')}</label>
                <div className="camera-sliders__slider-row">
                    <span className="camera-sliders__indicator">−</span>
                    <input
                        type="range"
                        className="camera-sliders__input"
                        min={MIN_ZOOM}
                        max={MAX_ZOOM}
                        step={0.1}
                        value={zoomValue}
                        onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
                        aria-label={t('position.zoom')}
                    />
                    <span className="camera-sliders__indicator">+</span>
                </div>
            </div>

            {/* Lift Slider */}
            <div className="camera-sliders__control">
                <label className="camera-sliders__label">{t('position.lift')}</label>
                <div className="camera-sliders__slider-row">
                    <span className="camera-sliders__indicator">−</span>
                    <input
                        type="range"
                        className="camera-sliders__input"
                        min={0}
                        max={100}
                        step={0.5}
                        value={liftValue}
                        onChange={(e) => handleModelLift(parseFloat(e.target.value))}
                        aria-label={t('position.lift')}
                    />
                    <span className="camera-sliders__indicator">+</span>
                </div>
            </div>

            {/* Pan Horizontal Slider */}
            <div className="camera-sliders__control">
                <label className="camera-sliders__label">{t('position.panHorizontal')}</label>
                <div className="camera-sliders__slider-row">
                    <span className="camera-sliders__indicator">←</span>
                    <input
                        type="range"
                        className="camera-sliders__input"
                        min={-100}
                        max={100}
                        step={1}
                        value={panHorizontal}
                        onChange={(e) => handlePanHorizontal(parseFloat(e.target.value))}
                        aria-label={t('position.panHorizontal')}
                    />
                    <span className="camera-sliders__indicator">→</span>
                </div>
            </div>
        </div>
    );
};

export default CameraSliders;
